<script setup>
import PopinRegisterCarrefourFid from "@/components/organisms/popin-register-carrefour-fid";
import PopinRegisterConfirmCarrefour from "@/components/organisms/popin-register-confirm-carrefour";
import PopinLockCardCarrefour from "@/components/organisms/popin-lock-card-carrefour";
import { useAxiosHelper } from "@/utils/axios-helper";

import Popin from "@/containers/popin-adaptive";

import fideliteApiDrive from "@/services/http/api-drive/client/fidelite";
import { useClientStore } from "@/store/client";

const { fetchFidelities, fetchClient } = useClientStore();

const props = defineProps({
  isAjoutCompte: {
    type: Boolean,
    required: true
  },
  carteCarrefour: {
    type: Object,
    default: null
  },
  typeCarteSelected: {
    type: String,
    required: true
  },
  cards: {
    type: Array,
    required: true
  }
});

const emit = defineEmits(["link"]);

const app = useNuxtApp();

const { getErrorStatus } = useAxiosHelper();

const isPrevalidate = ref(!!props.carteCarrefour);
const confirmPopin = ref(null);
const lockPopin = ref(null);
const error = ref(null);
const messageError = ref(null);
const isLoading = ref(false);

// First check the card number
const prevalidate = async (data) => {
  error.value = null;
  isLoading.value = true;
  messageError.value = null;

  try {
    await app.$apiDrive.use(fideliteApiDrive).prevalidateCarrefour(data);
    isPrevalidate.value = true;
  } catch (e) {
    const errorStatus = getErrorStatus(e);

    if (errorStatus === 423) {
      popinRegister.value.hide();
      lockPopin.value.show();
    } else if (errorStatus === 403) {
      messageError.value =
        "Vous avez dépassé le nombre de tentatives erronées autorisées, veuillez réessayer dans une heure.";
    } else if (errorStatus === 429) {
      messageError.value = "Vous avez dépassé le nombre de saisie autorisée";
    } else if (errorStatus !== 422) {
      messageError.value =
        e.response?.data?.message ??
        "Une erreur est survenue. Nos équipes techniques mettent tout en œuvre pour rétablir la situation.";
    }

    error.value = e.response?.data ?? null;
  } finally {
    isLoading.value = false;
  }
};

// Then check the card number & pin
const validate = async (form) => {
  error.value = null;
  messageError.value = null;
  isLoading.value = true;

  let data = {
    ...form
  };

  if (!props.isAjoutCompte) {
    data = {
      ...data,
      type_carte: props.typeCarteSelected
    };
  } else {
    data = {
      ...form
    };
  }

  try {
    if (!props.isAjoutCompte) {
      await app.$apiDrive.use(fideliteApiDrive).appairageCarrefour(data);
    } else {
      await app.$apiDrive.use(fideliteApiDrive).postFidelites(data);
    }
    await fetchFidelities();
    await fetchClient();
    popinRegister.value.hide();
    if (!!!props.carteCarrefour) {
      isPrevalidate.value = false;
    }
    confirmPopin.value.show();
  } catch (e) {
    const errorStatus = getErrorStatus(e);

    if (errorStatus === 423) {
      popinRegister.value.hide();
      lockPopin.value.show();
    } else if (errorStatus === 403) {
      messageError.value =
        "Vous avez dépassé le nombre de tentatives erronées autorisées, veuillez réessayer dans une heure.";
    } else if (errorStatus === 429) {
      messageError.value = "Vous avez dépassé le nombre de saisie autorisée";
    } else if (errorStatus !== 422) {
      messageError.value =
        e.response?.data?.message ??
        "Une erreur est survenue. Nos équipes techniques mettent tout en œuvre pour rétablir la situation.";
    }

    error.value = e.response?.data ?? null;
  } finally {
    isLoading.value = false;
  }
};

const popinRegister = ref(null);

const show = () => {
  popinRegister.value.show();
};

const cardsLinkable = computed(() => {
  return props.cards.filter((card) => {
    return !!!card.attributes.num_carte_carrefour;
  });
});

const linkAnotherCard = () => {
  isPrevalidate.value = false;
  confirmPopin.value.hide();
  emit("link");
  popinRegister.value.show();
};

const resetErrorPopin = () => {
  error.value = null;
  messageError.value = null;
  isLoading.value = false;
};

defineExpose({
  show
});
</script>

<template>
  <Popin
    ref="popinRegister"
    @hidden="resetErrorPopin"
  >
    <PopinRegisterCarrefourFid
      :is-ajout-compte="isAjoutCompte"
      :is-valid-card="isPrevalidate"
      :error="error"
      :message-error="messageError"
      :is-loading="isLoading"
      :carte-carrefour="carteCarrefour"
      @prevalidate="prevalidate"
      @validate="validate"
    />
  </Popin>
  <Popin ref="confirmPopin">
    <PopinRegisterConfirmCarrefour
      is-ajout-compte="isAjoutCompte"
      :type-carte="typeCarteSelected"
      :can-add-cpay="cardsLinkable.length >= 1"
      @close="confirmPopin.hide()"
      @link="linkAnotherCard"
    />
  </Popin>
  <Popin ref="lockPopin">
    <PopinLockCardCarrefour />
  </Popin>
</template>